/**
 * Hook that connects the canvas dispatch to the canvas tool handlers.
 *
 * Canvas tools are registered statically in createBrowserTools() so they're
 * always available for server discovery. This hook sets the dispatch ref so
 * the handlers can render to, read from, and control the canvas state.
 */

import { useEffect } from 'react';
import { setCanvasDispatch } from '../rpc/CanvasTools';
import type { UseCanvasReturn } from './useCanvas';

/**
 * Connect canvas dispatch to the statically-registered canvas tools.
 *
 * @param canvas - The return value from useCanvas()
 */
export function useCanvasTools(canvas: UseCanvasReturn): void {
  useEffect(() => {
    setCanvasDispatch({
      handleUpdate: canvas.handleUpdate,
      handleComplete: canvas.handleComplete,
      getState: () => ({
        canvases: canvas.canvases,
        activeCanvasId: canvas.activeCanvasId,
        panelVisible: canvas.panelVisible,
      }),
      setActiveCanvas: canvas.setActiveCanvas,
      closeCanvas: canvas.closeCanvas,
      closeComponent: canvas.closeComponent,
      clearAllCanvases: canvas.clearAllCanvases,
      togglePanel: canvas.togglePanel,
    });
    return () => setCanvasDispatch(null);
  }, [
    canvas.handleUpdate,
    canvas.handleComplete,
    canvas.canvases,
    canvas.activeCanvasId,
    canvas.panelVisible,
    canvas.setActiveCanvas,
    canvas.closeCanvas,
    canvas.closeComponent,
    canvas.clearAllCanvases,
    canvas.togglePanel,
  ]);
}
